import {Component, DestroyRef, OnInit, ViewChild} from '@angular/core';
import {PDF_CREATION_METHOD, PLAN_VALIDATION_STAGE, TABS} from '../common/constants';
import {PlanAmendmentComponent} from './plan-amendment/plan-amendment.component';
import {StagedSubdivisionComponent} from './staged-subdivision/staged-subdivision.component';
// eslint-disable-next-line max-len
import {CertificationSurveyRegulatoryStatementComponent} from './certification-survey-regulatory-statement/certification-survey-regulatory-statement.component';
import {VariationApprovalsComponent} from './variation-approvals/variation-approvals.component';
import {SurveyCommentsComponent} from './survey-comments/survey-comments.component';
import {NgEventBus} from 'ng-event-bus';
import {PermissionsService} from '../common/service/permissions.service';
import {LookupService} from '../common/service/lookup.service';
import {PlanModelService} from '../common/service/plan-model.service';
import {PlanLodgementService} from '../common/service/plan-lodgement.service';
import {CertificationType} from '../common/models/certification-type.model';
import {first, Observable, tap} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {FormsModule, NgForm} from '@angular/forms';
import {NotificationAlertsService} from '../common/notification-alerts/notification-alerts.service';
import {NlrPlanDetailService} from '../plan-detail/plan-detail.service';
import {RuleResultsResponse} from '../plan-detail/plan-detail.model';
import {NLR_TABS as LeftComponentTabs} from '../common/constants';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'nlr-certification',
    imports: [
        PlanAmendmentComponent,
        StagedSubdivisionComponent,
        CertificationSurveyRegulatoryStatementComponent,
        VariationApprovalsComponent,
        SurveyCommentsComponent,
        FormsModule
    ],
    templateUrl: './certification.component.html',
    styleUrl: './certification.component.scss'
})
export class CertificationComponent implements OnInit {
    private static readonly DEFAULT_CERTIFICATION_TYPE = 'Regulation 54';

    // Digital statutory plans (FH_STAT/CR_STAT lodged via Digital Plan) present generic wording rather than the Reg. 54
    // lookup text, so they seed this literal instead of certificationType.certificationText.
    private static readonly STATUTORY_CERTIFICATION_TEXT = 'For statutory purposes only.';

    @ViewChild('certificationForm')
    certificationForm: NgForm;

    protected readonly title = TABS.CERTIFICATION;

    certificationPermissions = ['EDIT_CERTIFICATION', 'VIEW_CERTIFICATION'];

    validatePermissions = ['EDIT_PLAN', 'VIEW_PLAN'];

    certificationTypes: CertificationType[];

    constructor(
        private destroyRef: DestroyRef,
        private eventBus: NgEventBus,
        private permissionsService: PermissionsService,
        private lookupService: LookupService,
        private planModelService: PlanModelService,
        private planLodgementService: PlanLodgementService,
        private notificationAlertsService: NotificationAlertsService,
        private nlrPlanDetailService: NlrPlanDetailService
    ) {}

    ngOnInit() {
        this.certificationTypes = this.lookupService.getLookups('certificationType');

        this.eventBus
            .on(`nlr:tabChange:${LeftComponentTabs.CERTIFICATION}`)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                const planModel = this.getPlanModel();

                if (!planModel.certificationType) {
                    if (planModel.surveyMethod?.certificationlookupId) {
                        planModel.certificationType = this.certificationTypes[planModel.surveyMethod?.certificationlookupId - 1];
                    } else {
                        // No survey method (e.g. digital plans where survey details are not displayed); default to Regulation 54.
                        planModel.certificationType = this.certificationTypes.find(
                            type => type.certificationType === CertificationComponent.DEFAULT_CERTIFICATION_TYPE
                        );
                    }
                }

                if (!planModel.certification) {
                    planModel.certification = {variationApprovals: []};
                }

                this.seedCertificationText();

                this.initVariationApprovals();

                this.eventBus.cast('nlr:initiate-plan-amendment');
            });

        this.eventBus
            .on('SAVE_CERTIFICATION')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this.save());

        this.eventBus
            .on<boolean>('VALIDATE_CERTIFICATION')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(meta => this.validate(meta.data));
    }

    getPlanModel() {
        return this.planModelService.getModel();
    }

    isDigitalOrPortalPlan() {
        const pdfCreationMethod = this.getPlanModel().pdfCreationMethod;
        return pdfCreationMethod == PDF_CREATION_METHOD.DIGITAL_PLAN || pdfCreationMethod == PDF_CREATION_METHOD.SURVEYOR_PORTAL;
    }

    isSaveVisible() {
        return this.permissionsService.hasPermission(this.certificationPermissions);
    }

    isSaveDisabled() {
        return (
            this.planModelService.readonly ||
            !this.permissionsService.hasEditPermission(this.certificationPermissions) ||
            !this.planLodgementService.hasMinimumPlanDetails()
        );
    }

    save() {
        console.debug(`${this.title} component save`);
        if (this.isSaveDisabled()) {
            return;
        }
        const subscription = this.planLodgementService.save().subscribe({
            next: () => this.initVariationApprovals(),
            complete: () => subscription.unsubscribe()
        });
        this.eventBus.cast('nlr:busy', subscription);
    }

    isValidateVisible() {
        return this.permissionsService.hasPermission(this.validatePermissions);
    }

    isValidateDisabled() {
        return (
            this.planModelService.readonly ||
            !this.planLodgementService.certificationApplicable() ||
            !this.planLodgementService.hasMinimumPlanDetails() ||
            !this.permissionsService.hasEditPermission(this.validatePermissions)
        );
    }

    validate(skipSave = false) {
        console.debug(`${this.title} component validate`);
        if (this.isValidateDisabled()) {
            return;
        }

        this.certificationForm.onSubmit(undefined);

        if (this.certificationForm.invalid) {
            this.notificationAlertsService.addMessage({
                subType: 'CertificationForm',
                text: 'The Certification page is incomplete or data entered is invalid!'
            });
        }

        let runValidations: Observable<RuleResultsResponse>;

        if (this.planModelService.readonly) {
            runValidations = this.runValidations();
        } else if (skipSave) {
            runValidations = this.runValidations().pipe(tap(() => this.initVariationApprovals()));
        } else {
            this.planLodgementService.increaseValidationAttempt();
            this.planModelService.sanitizeVariationApprovals();
            runValidations = this.planModelService.save().pipe(
                switchMap(() => this.runValidations()),
                tap(() => this.initVariationApprovals())
            );
        }

        const subscription = runValidations.pipe(first()).subscribe();

        this.eventBus.cast('nlr:busy', subscription);
    }

    isCancelVisible() {
        return this.permissionsService.hasPermission(this.certificationPermissions);
    }

    isCancelDisabled() {
        return this.planModelService.readonly || !this.permissionsService.hasEditPermission(this.certificationPermissions);
    }

    cancel() {
        console.debug(`${this.title} component - cancel`);

        this.planLodgementService.cancel();
    }

    private getCertification() {
        return this.getPlanModel().certification;
    }

    // Seed the certification text on tab open when it hasn't been set yet (e.g. a plan that reached this tab without a
    // survey-method change or a Reset click). Runs after certificationType and the certification object are established
    // above, so both are guaranteed to exist here. Any existing value (surveyor-entered or loaded from the DB) is preserved.
    private seedCertificationText() {
        const certification = this.getCertification();
        if (certification.certificationText) {
            return;
        }

        if (this.isDigitalStatutoryPlan()) {
            certification.certificationText = CertificationComponent.STATUTORY_CERTIFICATION_TEXT;
        } else if (this.getPlanModel().certificationType?.certificationText) {
            certification.certificationText = this.getPlanModel().certificationType.certificationText.replace(/<br>/g, '\n');
        }
    }

    private isDigitalStatutoryPlan() {
        return this.getPlanModel().pdfCreationMethod == PDF_CREATION_METHOD.DIGITAL_PLAN && this.isStatutoryPlan();
    }

    private isStatutoryPlan() {
        const id = this.getPlanModel().planPurpose?.id;
        return id === 'FH_STAT' || id === 'CR_STAT';
    }

    private initVariationApprovals() {
        const certification = this.getCertification();
        if (certification.variationApprovals.length === 0) {
            certification.variationApprovals.push({variationType: null, regulatoryStatement: '', approvalReference: ''});
        }
    }

    private runValidations() {
        return this.nlrPlanDetailService.runValidationsForStage(PLAN_VALIDATION_STAGE.LODGEMENT_CERTIFICATION);
    }
}
